// src/components/CookieBanner.jsx
import React, { useState, useEffect } from "react";

const NAV  = "#0B1F3A";
const NAVL = "#1e3a5c";
const DS   = { fontFamily: "'DM Sans',sans-serif" };
const PJS  = { fontFamily: "'Plus Jakarta Sans',sans-serif" };
const COOKIE_NAME = "broadway_cookie_consent";
const CONSENT_VERSION = 3;   // v3 = screen-sharing is handled outside cookie preferences
// ── SVG Icons ────────────────────────────────────────────────────────────────
const Icons = {
  Cookie: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
      <path d="M8.5 8.5v.01M16 15.5v.01M12 12v.01"/>
    </svg>
  ),
  Lock: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={NAV} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
  Chart: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/>
      <line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  Target: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </svg>
  ),
  Close: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Check: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
};

export default function CookieBanner() {
  const [visible,   setVisible]   = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const [animate,   setAnimate]   = useState(false);

  useEffect(() => {
    // "Cookie settings" link (footer / chat) re-opens the choices
    const reopen = () => {
      let c = null;
      try { c = JSON.parse(localStorage.getItem(COOKIE_NAME) || "null"); } catch (e) {}
      if (c) { setAnalytics(!!c.analytics); setMarketing(!!c.marketing); }
      setShowPrefs(true);
      setVisible(true);
      setTimeout(() => setAnimate(true), 50);
    };
    window.addEventListener("bh-open-cookie-settings", reopen);
    return () => window.removeEventListener("bh-open-cookie-settings", reopen);
  }, []);

  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(COOKIE_NAME) || "null"); } catch (e) {}
    if (!saved || (saved.v || 1) < CONSENT_VERSION) {
      const timer = setTimeout(() => {
        setVisible(true);
        setTimeout(() => setAnimate(true), 50);
      }, 4500);
      return () => clearTimeout(timer);
    }
  }, []);

  const close = (anal, mkt) => {
    // Screen sharing/recording is not a cookie category. Its policy and any required notice belong on the Privacy Policy page.
    const choice = { v: CONSENT_VERSION, analytics: anal, marketing: mkt, screen: true, date: new Date().toISOString() };
    try { localStorage.setItem(COOKIE_NAME, JSON.stringify(choice)); } catch (e) {}
    try { window.dispatchEvent(new CustomEvent("bh-consent", { detail: choice })); } catch (e) {}
    setAnimate(false);
    setTimeout(() => { setVisible(false); setShowPrefs(false); }, 350);
  };

  if (!visible) return null;

  return (
    <>
      {/* Overlay */}
      <div style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,.55)",
        backdropFilter: "blur(4px)",
        zIndex: 99997,
        opacity: animate ? 1 : 0,
        transition: "opacity .35s ease",
      }}/>

      {/* Centered modal */}
      <div style={{
        position: "fixed",
        top: "50%", left: "50%",
        transform: animate
          ? "translate(-50%,-50%) scale(1)"
          : "translate(-50%,-48%) scale(.96)",
        zIndex: 99999,
        width: "calc(100% - 32px)",
        maxWidth: 480,
        opacity: animate ? 1 : 0,
        transition: "all .35s cubic-bezier(.16,1,.3,1)",
      }}>
        <div style={{
          background: "#fff",
          borderRadius: 20,
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(0,0,0,.3)",
        }}>

          {!showPrefs ? (
            <>
              {/* Header */}
              <div style={{
                background: `linear-gradient(135deg,${NAV},${NAVL})`,
                padding: "22px 24px 20px",
                display: "flex", alignItems: "center", gap: 14,
              }}>
                <div style={{
                  width: 50, height: 50, borderRadius: 14,
                  background: "rgba(255,255,255,.12)",
                  display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0,
                }}>
                  <Icons.Cookie/>
                </div>
                <div>
                  <h2 style={{ fontSize: 17, fontWeight: 900, color: "#fff", margin: "0 0 3px", ...PJS }}>
                    We use cookies
                  </h2>
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,.65)", margin: 0, ...DS }}>
                    Broadway Holidays · broadwayholidays.co.uk
                  </p>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: "20px 24px" }}>
                <p style={{ fontSize: 13, color: "#475569", lineHeight: 1.75, margin: "0 0 18px", ...DS }}>
                  We use cookies to improve your browsing experience, analyse site traffic and show
                  personalised content. With your OK, our travel experts can also can help you with chat bot to find best deals{" "}
              
                  and book the right holiday.
                  <strong style={{ color: NAV }}>"Accept All"</strong> you agree to all of these.
                  "Reject All" turns them all off. You can change your choice at any time.{" "}
                  <a href="/privacy" style={{ color: "#059669", fontWeight: 600, textDecoration: "none" }}>
                  
                  </a>
                </p>

                {/* Cookie types */}
                <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                  {[
                    { Icon: Icons.Lock,   label: "Essential",  bg: "#f0f4ff", border: "#c7d2fe" },
                    { Icon: Icons.Chart,  label: "Analytics",  bg: "#f0f9ff", border: "#bae6fd" },
                    { Icon: Icons.Target, label: "Marketing",  bg: "#faf5ff", border: "#e9d5ff" },
                  ].map((c, i) => (
                    <div key={i} style={{
                      flex: 1, background: c.bg,
                      border: `1px solid ${c.border}`,
                      borderRadius: 10, padding: "10px 6px", textAlign: "center",
                    }}>
                      <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
                        <c.Icon/>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: NAV, ...DS }}>{c.label}</div>
                    </div>
                  ))}
                </div>

                {/* Buttons */}
                <button onClick={() => close(true, true)} style={{
                  width: "100%", padding: "13px",
                  background: NAV, color: "#fff",
                  border: "none", borderRadius: 10,
                  fontSize: 15, fontWeight: 800,
                  cursor: "pointer", marginBottom: 8, ...PJS,
                  display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 8,
                }}>
                  <Icons.Check/> Accept All Cookies
                </button>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <button onClick={() => close(false, false)} style={{
                    padding: "11px",
                    background: "#f8fafc", color: "#64748b",
                    border: "1.5px solid #e2e8f0", borderRadius: 10,
                    fontSize: 13, fontWeight: 600,
                    cursor: "pointer", ...DS,
                  }}>
                    Reject All
                  </button>
                  <button onClick={() => setShowPrefs(true)} style={{
                    padding: "11px",
                    background: "#f8fafc", color: NAV,
                    border: "1.5px solid #e2e8f0", borderRadius: 10,
                    fontSize: 13, fontWeight: 600,
                    cursor: "pointer", ...DS,
                  }}>
                    Manage
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div style={{
                padding: "10px 24px 16px",
                textAlign: "center",
                fontSize: 11, color: "#cbd5e1", ...DS,
                borderTop: "1px solid #f1f5f9",
              }}>
                Broadway Holidays · Registered in England & Wales
              </div>
            </>
          ) : (

            /* ── PREFERENCES VIEW ── */
            <>
              <div style={{
                background: `linear-gradient(135deg,${NAV},${NAVL})`,
                padding: "18px 24px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 900, color: "#fff", margin: 0, ...PJS }}>
                    Cookie Preferences
                  </h2>
                  <p style={{ fontSize: 11, color: "rgba(255,255,255,.6)", margin: "2px 0 0", ...DS }}>
                    Choose what you allow
                  </p>
                </div>
                <button onClick={() => setShowPrefs(false)} style={{
                  background: "rgba(255,255,255,.12)", border: "none",
                  borderRadius: 8, width: 32, height: 32,
                  cursor: "pointer", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Icons.Close/>
                </button>
              </div>

              <div style={{ padding: "16px 24px" }}>
                {[
                  {
                    Icon: Icons.Lock,
                    label: "Essential Cookies",
                    desc: "Required for the site to work. Cannot be disabled.",
                    locked: true, value: true,
                    bg: "#f0f4ff",
                  },
                  {
                    Icon: Icons.Chart,
                    label: "Analytics Cookies",
                    desc: "Help us improve the site via Google Analytics. Anonymous data only.",
                    locked: false, value: analytics, set: setAnalytics,
                    bg: "#f0f9ff",
                  },
                  {
                    Icon: Icons.Target,
                    label: "Marketing Cookies",
                    desc: "Used to show relevant ads on Facebook, Google and other platforms.",
                    locked: false, value: marketing, set: setMarketing,
                    bg: "#faf5ff",
                  },
                
                ].map((c, i, arr) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "flex-start", gap: 12,
                    padding: "14px 0",
                    borderBottom: i < arr.length - 1 ? "1px solid #f1f5f9" : "none",
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: c.bg,
                      display: "flex", alignItems: "center",
                      justifyContent: "center", flexShrink: 0,
                    }}>
                      <c.Icon/>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: NAV, marginBottom: 2, ...DS }}>{c.label}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", ...DS, lineHeight: 1.5 }}>{c.desc}</div>
                    </div>
                    {c.locked ? (
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: "#059669",
                        background: "#ecfdf5", borderRadius: 20,
                        padding: "4px 10px", whiteSpace: "nowrap",
                        flexShrink: 0, ...DS, marginTop: 4,
                      }}>Always On</span>
                    ) : (
                      <div onClick={() => c.set(!c.value)} style={{
                        width: 46, height: 26, borderRadius: 13,
                        cursor: "pointer", flexShrink: 0, marginTop: 4,
                        background: c.value ? "#059669" : "#d1d5db",
                        position: "relative", transition: "background .2s",
                      }}>
                        <div style={{
                          width: 20, height: 20, borderRadius: "50%",
                          background: "#fff", position: "absolute", top: 3,
                          left: c.value ? 23 : 3, transition: "left .2s",
                          boxShadow: "0 1px 4px rgba(0,0,0,.2)",
                        }}/>
                      </div>
                    )}
                  </div>
                ))}

                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <button onClick={() => close(false, false)} style={{
                    flex: 1, padding: "11px",
                    background: "#f8fafc", color: "#64748b",
                    border: "1.5px solid #e2e8f0", borderRadius: 10,
                    fontSize: 13, fontWeight: 600, cursor: "pointer", ...DS,
                  }}>Reject All</button>
                  <button onClick={() => close(analytics, marketing)} style={{
                    flex: 1, padding: "11px",
                    background: NAV, color: "#fff",
                    border: "none", borderRadius: 10,
                    fontSize: 13, fontWeight: 700, cursor: "pointer", ...PJS,
                    display: "flex", alignItems: "center",
                    justifyContent: "center", gap: 6,
                  }}>
                    <Icons.Check/> Save & Continue
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}